import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    CreateOperationTemplateRequest,
    SaveOperationDraftRequest,
} from '@/api/handlers/resources/operations/requests';
import type { GetOperationTemplateResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

/**
 * The four writes the editor makes.
 *
 * All of them invalidate the **whole** operations key, not just the template
 * they touched. That is deliberate rather than lazy: every one of them changes a
 * row on the catalog — a new row after a create, the version number and
 * effective date after a publish, the status chip after a block, the
 * who-may-record chips after a save — and the catalog is a short list, so a
 * targeted invalidation would be bookkeeping with no payoff.
 *
 * None of them reject: callers branch on `result.success` and read
 * `result.error.code`, which is the codebase-wide envelope contract.
 */

/**
 * Mint a brand-new template.
 *
 * Returns the created detail so the caller can navigate straight to its
 * editor — `BIND_EDIT(response.key)` — without a second round trip.
 */
export const useCreateOperationTemplateMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationTemplateResponse>,
        Error,
        CreateOperationTemplateRequest
    >({
        mutationFn: (body) => apiClient.operations.createTemplate(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};

export type SaveOperationDraftVariables = {
    key: string;
    body: SaveOperationDraftRequest;
};

/**
 * Create or replace the template's single draft.
 *
 * Safe by construction: a draft changes nothing anyone records, so this can be
 * called on half-finished work. Only `useActivateOperationVersionMutation`
 * changes what the books will do.
 */
export const useSaveOperationDraftMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationTemplateResponse>,
        Error,
        SaveOperationDraftVariables
    >({
        mutationFn: ({ key, body }) =>
            apiClient.operations.saveDraft(key, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};

export type ActivateOperationVersionVariables = {
    key: string;
    version: number;
    /** ISO `YYYY-MM-DD`. Omitted keeps the draft's own date. */
    effectiveFrom?: string;
};

/**
 * Publish a draft as the version in force from a date.
 *
 * **Irreversible.** From this moment the version is frozen, because entries will
 * post under it — there is no un-publish and no edit. The screen confirms before
 * calling this, and the confirmation says so.
 */
export const useActivateOperationVersionMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationTemplateResponse>,
        Error,
        ActivateOperationVersionVariables
    >({
        mutationFn: ({ key, version, effectiveFrom }) =>
            apiClient.operations.activateVersion(key, version, effectiveFrom),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};

export type SetOperationBlockedVariables = {
    key: string;
    blocked: boolean;
};

/**
 * Retire an operation, or bring one back.
 *
 * Prospective only, and the confirm dialog quotes that rule: the operation stops
 * being offered for recording, and every entry it has already posted is
 * untouched. There is no delete — for the reason the chart of accounts has no
 * delete.
 */
export const useSetOperationBlockedMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationTemplateResponse>,
        Error,
        SetOperationBlockedVariables
    >({
        mutationFn: ({ key, blocked }) =>
            apiClient.operations.setTemplateBlocked(key, blocked),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};
